import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Save, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { invalidatePermissionsCache } from "@/lib/permissions";

/**
 * Admin-only page to edit the role × section permission matrix.
 *
 * 05/02/2026 — Novo sistema de permissões por secção do LeadDetail:
 *   * 4 secções críticas: financeiro, proposta, plano_cuidados, rt_consulta
 *   * 3 níveis por célula: nenhum / ver / editar
 *   * Fonte da verdade em MongoDB via ``/api/permissions`` (admin-editable).
 *   * O admin nunca perde ``edit`` (anti-lockout no backend).
 */

const ROLE_LABELS = {
  admin:        "Admin",
  direcao:      "Direção de Rede",
  gestor:       "Gestor de Cliente",
  consultor:    "Consultor de Cuidados",
  dt:           "Direção Técnica",
  dgo:          "DGO",
  recrutamento: "Recrutamento",
  atendimento:  "Atendimento",
};

const SECTION_LABELS = {
  financeiro:      "Financeiro",
  proposta:        "Proposta",
  plano_cuidados:  "Plano de Cuidados",
  rt_consulta:     "RT / Consulta Técnica",
};

const SECTION_HINTS = {
  financeiro:      "Estimativa, Custo AF, Margem, Caução, Pagamentos",
  proposta:        "Editor de proposta, envio, aceitação",
  plano_cuidados:  "Plano Preliminar + PIC (com DT)",
  rt_consulta:     "Agendar, marcar concluída, resumo RT",
};

const LEVEL_LABELS = { none: "Nenhum", view: "Ver", edit: "Editar" };
const LEVEL_STYLES = {
  none: "border-border/70 bg-muted text-muted-foreground",
  view: "border-primary/30 bg-primary/5 text-primary",
  edit: "border-[hsl(var(--success))]/30 bg-[hsl(var(--success-bg))] text-[hsl(var(--success))]",
};

export default function PermissionsSettings() {
  const [sections, setSections] = useState([]);
  const [levels, setLevels] = useState([]);
  const [matrix, setMatrix] = useState({});
  const [initial, setInitial] = useState({});
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/permissions");
      setSections(data.sections || []);
      setLevels(data.levels || ["none", "view", "edit"]);
      const m = data.matrix || {};
      setMatrix(m);
      setInitial(JSON.parse(JSON.stringify(m)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro a carregar permissões");
    } finally {
      setLoading(false);
    }
  }

  const isDirty = JSON.stringify(matrix) !== JSON.stringify(initial);

  const setCell = (role, section, level) => {
    if (role === "admin") return;  // admin nunca é editável (anti-lockout)
    setMatrix((prev) => ({
      ...prev,
      [role]: { ...(prev[role] || {}), [section]: level },
    }));
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/permissions", { matrix });
      setMatrix(data.matrix);
      setInitial(JSON.parse(JSON.stringify(data.matrix)));
      invalidatePermissionsCache();
      toast.success("Matriz de permissões guardada — refresca a página para os utilizadores verem o efeito.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro a gravar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto py-10 px-6 space-y-8" data-testid="permissions-page">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center">
          <ShieldCheck className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
            Organização · Segurança
          </div>
          <h1 className="font-display text-3xl">Permissões por Role</h1>
          <p className="text-sm text-muted-foreground max-w-3xl">
            Controla, por role, o que cada utilizador vê e edita nas <b>4 secções críticas</b> do lead.
            O <b>admin</b> mantém sempre <i>edit</i> em todas (anti-lockout). Depois de gravar,
            os utilizadores afectados precisam de refresh para o novo nível ficar activo.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="perm-reload"
            onClick={load}
            disabled={busy || loading}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card hover:bg-muted text-sm font-semibold px-3 py-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Recarregar
          </button>
          <button
            data-testid="perm-save"
            onClick={save}
            disabled={busy || loading || !isDirty}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground text-sm font-semibold px-4 py-2 disabled:opacity-40"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Gravar matriz
          </button>
        </div>
      </div>

      {loading ? (
        <div className="rounded-xl border border-border bg-card p-8 flex items-center justify-center gap-2 text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> A carregar…
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50">
                <th className="text-left text-[10px] uppercase tracking-widest text-muted-foreground font-semibold p-3">
                  Role
                </th>
                {sections.map((s) => (
                  <th key={s} className="text-left text-[10px] uppercase tracking-widest text-muted-foreground font-semibold p-3">
                    <div>{SECTION_LABELS[s] || s}</div>
                    <div className="text-[9px] normal-case tracking-normal text-muted-foreground/70 font-normal max-w-[220px]">
                      {SECTION_HINTS[s] || ""}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.keys(ROLE_LABELS).map((role) => (
                <tr key={role} className="border-t border-border">
                  <td className="p-3 align-top">
                    <div className="font-semibold">{ROLE_LABELS[role]}</div>
                    <div className="text-[10px] text-muted-foreground uppercase tracking-widest">{role}</div>
                    {role === "admin" && (
                      <div className="mt-1 text-[10px] text-muted-foreground italic">
                        (sempre editar — protegido)
                      </div>
                    )}
                  </td>
                  {sections.map((s) => {
                    const cur = matrix[role]?.[s] || "none";
                    const readOnly = role === "admin";
                    return (
                      <td key={s} className="p-3 align-top">
                        <div className={`inline-flex overflow-hidden rounded-full border ${
                          readOnly ? "opacity-70 cursor-not-allowed" : ""
                        }`}>
                          {levels.map((lvl) => {
                            const active = lvl === cur;
                            return (
                              <button
                                key={lvl}
                                type="button"
                                disabled={readOnly}
                                onClick={() => setCell(role, s, lvl)}
                                data-testid={`cell-${role}-${s}-${lvl}`}
                                className={`px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest border-r last:border-r-0 border-border/60 ${
                                  active
                                    ? LEVEL_STYLES[lvl]
                                    : "bg-card text-muted-foreground hover:bg-muted"
                                }`}
                              >
                                {LEVEL_LABELS[lvl]}
                              </button>
                            );
                          })}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="rounded-md border border-dashed border-border bg-muted/30 p-4 text-[13px] text-muted-foreground">
        <b>Como funciona no LeadDetail:</b>
        <ul className="list-disc list-inside mt-1 space-y-0.5">
          <li><b>Nenhum</b> → a secção desaparece completamente da UI.</li>
          <li><b>Ver</b> → a secção aparece mas todos os botões de edição/envio ficam desactivados.</li>
          <li><b>Editar</b> → acesso completo (mesmo comportamento actual).</li>
        </ul>
      </div>
    </div>
  );
}
